import type { HTMLAttributes, PropsWithChildren } from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const ALERT_ASSET_GRID_CLASS_MAP = {
  default: "",
  withIcon: "grid-cols-[theme(spacing.4)_1fr]",
} as const;

export const ALERT_COPY_COLUMN_CLASS_MAP = {
  default: "col-start-1",
  withAsset: "col-start-2",
} as const;

export const ALERT_VARIANT_CLASS_MAP = {
  subtle: "border-component-alert-subtle bg-component-alert-subtle [&_[data-component='AlertBody']]:text-foreground-muted-strong [&_[data-component='AlertTitle']]:text-foreground [&_[data-component='Icon']]:text-foreground",
  info: "border-component-alert-info bg-component-alert-info [&_[data-component='AlertBody']]:text-foreground [&_[data-component='AlertTitle']]:text-functional-info [&_[data-component='Icon']]:text-functional-info",
  reassurace: "border-component-alert-reassurace bg-component-alert-reassurace [&_[data-component='AlertBody']]:text-foreground [&_[data-component='AlertTitle']]:text-functional-reassurance [&_[data-component='Icon']]:text-functional-reassurance",
  success: "border-component-alert-success bg-component-alert-success [&_[data-component='AlertBody']]:text-foreground-muted-strong [&_[data-component='AlertTitle']]:text-functional-success [&_[data-component='Icon']]:text-functional-success",
  destructive: "border-component-alert-destructive bg-component-alert-destructive [&_[data-component='AlertBody']]:text-foreground-muted-strong [&_[data-component='AlertTitle']]:text-functional-destructive [&_[data-component='Icon']]:text-functional-destructive",
  fyi: "border-component-alert-fyi bg-component-alert-fyi [&_[data-component='AlertBody']]:text-foreground [&_[data-component='AlertTitle']]:text-foreground [&_[data-component='Icon']]:text-foreground",
} as const;


/*-------------------------------------
|  Types
-------------------------------------*/

export type TAlertVariant = keyof typeof ALERT_VARIANT_CLASS_MAP;
export type TAlertAsset = TIconKey;

export type TAlertAssetProps = {
  asset?: TAlertAsset;
};

type TAlertBaseProps = TWithDataComponent & PropsWithChildren & HTMLAttributes<HTMLDivElement>;

export type TAlertWrapperProps = TAlertAssetProps & TAlertBaseProps;

export type TAlertProps = {
  title?: string;
  variant?: TAlertVariant;
} & TAlertWrapperProps;

export type TAlertTitleProps = Pick<TAlertProps, "title" | "asset">;

export type TAlertBodyProps = { hasTitle?: boolean } & Pick<TAlertProps, "asset"> & PropsWithChildren;

type TAlertWrapperDefaultProps = DefinedDefaults<
  TAlertProps,
  "data-component"
>;

type TAlertDefaultProps = DefinedDefaults<
  TAlertProps,
  "variant"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const ALERT_WRAPPER_DEFAULT_PROPS = {
  "data-component": "AlertWrapper",
} satisfies TAlertWrapperDefaultProps;

export const ALERT_DEFAULTS = {
  variant: "subtle",
} satisfies TAlertDefaultProps;
